import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';
import { PaginationConst } from '../constants/AppConstant';

export interface Product {
  id: string;
  name: string;
  description?: string;
  category: string; // SAAS, CLOTHES, FINANCIAL_PRODUCTS, CONSULTING_SERVICES
  price?: number;
  currency?: string;
  imageUrl?: string;
  subscriptionLink?: string;
  isPublished?: boolean;
  features?: string;
  tags?: string;
  status?: string;
  createdDate?: string;
  updatedDate?: string;
}

export interface ProductInterestRequest {
  productId: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  message?: string;
}

export async function getProducts(pageNumber: number = 0, pageSize: number = PaginationConst.PAGE_SIZE, filters: any = {}) {
  const queryParams = new URLSearchParams({
    page: pageNumber.toString(),
    size: pageSize.toString(),
  });
  
  if (filters.category) queryParams.append('category.equals', filters.category);
  if (filters.status) queryParams.append('status.equals', filters.status);
  if (filters.isPublished !== undefined) queryParams.append('isPublished.equals', filters.isPublished.toString());
  if (filters.name) queryParams.append('name.contains', filters.name);
  
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/products?${queryParams.toString()}`,
  });
  return response.data || response;
}

export async function getProduct(id: string) {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/products/${id}`,
  });
  return response.data || response;
}

export async function createProduct(data: Partial<Product>) {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/products`,
    data: data,
  });
  return response;
}

export async function updateProduct(id: string, data: Partial<Product>) {
  const response = await HttpClient.PUT({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/products/${id}`,
    data: data,
  });
  return response;
}

export async function deleteProduct(id: string) {
  const response = await HttpClient.DELETE({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/products/${id}`,
  });
  return response;
}

export async function publishProduct(id: string) {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/products/${id}/publish`,
  });
  return response;
}

export async function unpublishProduct(id: string) {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/products/${id}/unpublish`,
  });
  return response;
}

// Customer-facing APIs
export async function getCustomerProducts(pageNumber: number = 0, pageSize: number = PaginationConst.PAGE_SIZE, category?: string) {
  const queryParams = new URLSearchParams({
    page: pageNumber.toString(),
    size: pageSize.toString(),
  });
  
  if (category && category !== 'ALL') {
    queryParams.append('category', category);
  }
  
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/customer/products?${queryParams.toString()}`,
  });
  return response.data || response;
}

export async function submitInterestRequest(productId: string, data: ProductInterestRequest) {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/customer/products/${productId}/interest`,
    data: data,
  });
  return response.data || response;
}

// Admin APIs for Interest Requests
export interface InterestRequest {
  id: string;
  productId: string;
  borrowerId: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  message?: string;
  status: string;
  createdDate?: string;
  updatedDate?: string;
}

export async function getAllInterestRequests(status?: string, productId?: string) {
  const queryParams = new URLSearchParams();
  if (status) queryParams.append('status', status);
  if (productId) queryParams.append('productId', productId);
  
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/admin/interest-requests${queryParams.toString() ? '?' + queryParams.toString() : ''}`,
  });
  return response.data || response;
}

export async function updateInterestRequestStatus(id: string, status: string) {
  const response = await HttpClient.PUT({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/admin/interest-requests/${id}/status`,
    data: { status },
  });
  return response.data || response;
}

// Customer-facing API for interest requests
export async function getCustomerInterestRequests() {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/customer/interest-requests`,
  });
  return response.data || response;
}
